import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";

import { API_BASE } from "@/api/config";
import { ApiError, errorFrom } from "@/api/errors";
import { adoptSession } from "@/api/session";
import { AccountCard, AccountFrame, FormAlert } from "@/app/AccountFrame";
import { HOME_PATH } from "@/app/registry";
import { brand } from "@/brand";
import { Button, buttonClass, Field, Input, Skeleton } from "@/components/ui";
import { cn } from "@/lib/cn";

/** POST /platform/email/verify and /platform/email/confirm (V034). */
export interface Verified {
  status: "confirmed" | "already_confirmed";
  active: boolean;
  reason: string;
  kind: string;
  role: string | null;
  invitations_linked: number;
  /** V070: confirming for the first time signs in (one tap); never for an old link. */
  session?: Parameters<typeof adoptSession>[0] | null;
}

/** Signed in by the confirmation: into the app, like after sign-in (an
 * inactive plan opens Account and Payment there). */
function useEnterApp(): (result: Verified) => boolean {
  const navigate = useNavigate();
  return useCallback(
    (result) => {
      if (!result.session) return false;
      adoptSession(result.session);
      void navigate({ to: HOME_PATH, replace: true });
      return true;
    },
    [navigate],
  );
}

async function publicPost<T>(path: string, body: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(body),
    });
  } catch {
    throw new ApiError(0, null);
  }
  if (!res.ok) throw await errorFrom(res);
  return (await res.json()) as T;
}

function message(err: unknown): string {
  if (err instanceof ApiError && err.status === 0) return "Can't reach the server. Try again.";
  if (err instanceof ApiError && err.status === 429) return "Too many tries. Wait a little and try again.";
  const text = err instanceof Error ? err.message : "Something went wrong. Try again.";
  return text.charAt(0).toUpperCase() + text.slice(1);
}

const LINK = "font-medium text-brand underline-offset-4 hover:underline";

/**
 * Confirm the email address: opened from the emailed link (uid + token), or
 * without one to enter the 6-digit code from the same email, or to ask for a
 * new link. Works signed out: sign-in needs a confirmed address.
 */
export function VerifyEmailPage() {
  const search = useSearch({ strict: false }) as { uid?: string; token?: string; email?: string };
  const uid = Number(search.uid);
  const hasLink = !!search.token && Number.isInteger(uid) && uid > 0;
  return (
    <AccountFrame title="Confirm your email">
      <div className="mx-auto w-full max-w-[520px]">
        {hasLink ? <FromLink uid={uid} token={search.token as string} /> : <WithCode initialEmail={search.email ?? ""} />}
      </div>
    </AccountFrame>
  );
}

function FromLink({ uid, token }: { uid: number; token: string }) {
  const [result, setResult] = useState<Verified | null>(null);
  const [error, setError] = useState<string | null>(null);
  const sent = useRef(false);
  const enter = useEnterApp();

  useEffect(() => {
    if (sent.current) return; // the token is single-use: one request even in StrictMode
    sent.current = true;
    // POST from the page, never on GET: a mail scanner opening the link doesn't spend it
    publicPost<Verified>("/platform/email/verify", { uid, token })
      .then((out) => {
        setResult(out);
        enter(out);
      })
      .catch((err: unknown) => setError(message(err)));
  }, [uid, token, enter]);

  if (result?.session) return <Opening />;
  if (result) return <Confirmed result={result} />;
  if (error)
    return (
      <AccountCard title="This link has expired" intro="Confirmation links work once and for 7 days.">
        <FormAlert>{error}</FormAlert>
        <ResendLink />
      </AccountCard>
    );
  return (
    <AccountCard title="Confirming your email…">
      <Skeleton className="h-16 w-full" />
    </AccountCard>
  );
}

function Opening() {
  return (
    <AccountCard title="Email confirmed">
      <p className="rounded-lg bg-up-soft px-3 py-2 text-sm text-fg" role="status">
        Thank you. Opening your {brand.name} account…
      </p>
    </AccountCard>
  );
}

/** "Email confirmed" and what happens next, when this didn't sign in (an old
 * link, or sign-in isn't configured on the server). */
export function Confirmed({ result }: { result: Verified }) {
  const again = result.status === "already_confirmed";
  if (again)
    return (
      <AccountCard title="Email already confirmed">
        <p className="rounded-lg bg-up-soft px-3 py-2 text-sm text-fg" role="status">
          Your email is already confirmed. Sign in to continue.
        </p>
        <Link to="/login" className={cn(buttonClass("primary", "lg"), "w-full")}>
          Sign in
        </Link>
      </AccountCard>
    );
  return (
    <AccountCard title="Email confirmed">
      <p className="rounded-lg bg-up-soft px-3 py-2 text-sm text-fg" role="status">
        {again
          ? "This email address was confirmed earlier. Nothing more to do here."
          : `Thank you. Your email address is confirmed for your ${brand.name} account.`}
      </p>
      {/* invitations_linked: RA invitations ended with one kind of account (2026-10-25), so it isn't shown */}
      {result.active ? (
        <>
          <p className="text-sm text-muted">Your plan is active: sign in to start.</p>
          <Link to="/login" className={cn(buttonClass("primary", "lg"), "w-full")}>
            Sign in
          </Link>
        </>
      ) : (
        <>
          <p className="text-sm text-muted">
            {result.reason === "expired" || result.reason === "trial_ended"
              ? "Your plan has ended. Sign in to renew it: the page shows what to pay and where."
              : "Next: your payment. Sign in and pay online by UPI, card or net banking: your account is active as soon as the payment goes through, and we email you a receipt."}
          </p>
          <Link to="/login" className={cn(buttonClass("secondary", "lg"), "w-full")}>
            Go to sign in
          </Link>
        </>
      )}
    </AccountCard>
  );
}

function WithCode({ initialEmail }: { initialEmail: string }) {
  const [email, setEmail] = useState(initialEmail);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<Verified | null>(null);
  const enter = useEnterApp();

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const out = await publicPost<Verified>("/platform/email/confirm", { email, code });
      setResult(out);
      enter(out);
    } catch (err) {
      setError(message(err));
    } finally {
      setBusy(false);
    }
  }

  if (result?.session) return <Opening />;
  if (result) return <Confirmed result={result} />;
  return (
    <div className="grid gap-4">
      <AccountCard
        title="Confirm your email"
        intro="Tap the button in the email we sent you. If it doesn't open, enter the 6-digit code from the bottom of the same email here."
      >
        <form onSubmit={onSubmit} className="grid gap-4" noValidate>
          <Field label="Email" htmlFor="ve-email">
            <Input
              id="ve-email"
              type="email"
              className="h-11"
              inputMode="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </Field>
          <Field label="Code" htmlFor="ve-code" hint="6 digits, valid for 24 hours">
            <Input
              id="ve-code"
              className="num h-11 tracking-[0.3em]"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            />
          </Field>
          {error ? <FormAlert>{error}</FormAlert> : null}
          <Button
            type="submit"
            size="lg"
            loading={busy}
            disabled={!email.includes("@") || code.length !== 6}
            className="w-full"
          >
            Confirm my email
          </Button>
        </form>
      </AccountCard>
      <AccountCard title="Didn't get the email?">
        <ResendLink initialEmail={email} />
      </AccountCard>
    </div>
  );
}

/** "Send a new link": same answer whether or not the address has an account. */
export function ResendLink({ initialEmail = "" }: { initialEmail?: string }) {
  const [email, setEmail] = useState(initialEmail);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await publicPost("/platform/email/resend", { email });
      setSent(true);
    } catch (err) {
      setError(message(err));
    } finally {
      setBusy(false);
    }
  }

  if (sent)
    return (
      <div className="grid gap-3">
        <p className="text-sm text-muted" role="status">
          If <span className="font-medium [overflow-wrap:anywhere] text-fg">{email}</span> has an account that
          isn't confirmed yet, we've sent it a new link. Check your inbox and spam folder; the link works for 7 days.
        </p>
        <p className="text-sm">
          <Link to="/login" className={LINK}>
            Back to sign in
          </Link>
        </p>
      </div>
    );
  return (
    <form onSubmit={onSubmit} className="grid gap-3" noValidate>
      <Field label="Email" htmlFor="rl-email" hint="We'll email you a new confirmation link.">
        <Input
          id="rl-email"
          type="email"
          className="h-11"
          inputMode="email"
          autoComplete="username"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </Field>
      {error ? <FormAlert>{error}</FormAlert> : null}
      <Button type="submit" variant="secondary" size="lg" loading={busy} disabled={!email.includes("@")} className="w-full">
        Send a new link
      </Button>
    </form>
  );
}
